import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import Button from "designV2/components/Button";
import AuthShell, {
  CodeInput,
  clearCodeTimer,
  formatSeconds,
  go,
  readCodeTimer,
  readableError,
  startCodeTimer,
  useCountdown,
} from "./auth/AuthShell";

const CHECK_CODE = gql`
  mutation checkAdminForgotPasswordCode(
    $emailOrPhoneNumber: String!
    $emailOrPhoneNumberForgotPasswordCode: String!
  ) {
    checkAdminForgotPasswordCode(
      checkAdminForgotPasswordInput: {
        emailOrPhoneNumber: $emailOrPhoneNumber
        emailOrPhoneNumberForgotPasswordCode: $emailOrPhoneNumberForgotPasswordCode
      }
    ) {
      changePasswordCode
    }
  }
`;

const RESEND_CODE = gql`
  mutation getAdminForgotPasswordCode($emailOrPhoneNumber: String!) {
    getAdminForgotPasswordCode(emailOrPhoneNumber: $emailOrPhoneNumber) {
      phoneForgotPasswordCodeExpireTime
    }
  }
`;

const RESEND_COOLDOWN_SECONDS = 30;

/**
 * EnterForgetPasswordCode - confirm the reset code.
 *
 * A wrong code used to leave the Continue button disabled for good,
 * because nothing reset it on the way out of the failure.
 */
const EnterForgetPasswordCode = (props) => {
  const history = props.history;
  const identifier = window.localStorage.getItem("emailForgetPassword");

  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [checking, setChecking] = useState(false);

  const [remaining, setRemaining] = useCountdown(readCodeTimer());
  const [resendIn, setResendIn] = useCountdown(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);

  const [checkAdminForgotPasswordCode] = useMutation(CHECK_CODE);
  const [getAdminForgotPasswordCode] = useMutation(RESEND_CODE);

  // Takes the code rather than reading state, so the auto-submit that
  // fires as the last digit lands does not send the previous value.
  const submit = (value) => {
    if (checking || remaining === 0) return;

    if (value.length !== 5) {
      setCodeError("Enter all five digits");
      return;
    }

    setCodeError("");
    setChecking(true);
    checkAdminForgotPasswordCode({
      variables: {
        emailOrPhoneNumber: identifier,
        emailOrPhoneNumberForgotPasswordCode: value,
      },
    })
      .then((res) => {
        window.localStorage.setItem(
          "changePasswordCode",
          res.data.checkAdminForgotPasswordCode.changePasswordCode
        );
        clearCodeTimer();
        go(history, "/forgetPasswordChange");
      })
      .catch((error) => {
        setChecking(false);
        setCodeError(readableError(error));
      });
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    submit(code);
  };

  const onResend = () => {
    if (resending || resendIn > 0) return;

    setResending(true);
    setCodeError("");
    getAdminForgotPasswordCode({ variables: { emailOrPhoneNumber: identifier } })
      .then((res) => {
        const seconds = Number(
          res.data.getAdminForgotPasswordCode.phoneForgotPasswordCodeExpireTime
        );
        startCodeTimer(seconds);
        setRemaining(seconds);
        setResendIn(RESEND_COOLDOWN_SECONDS);
        setCode("");
        setResending(false);
      })
      .catch((error) => {
        setResending(false);
        setCodeError(readableError(error));
      });
  };

  if (!identifier) {
    return (
      <AuthShell
        title="Start again"
        subtitle="We do not have an account to reset."
        brandTitle="Locked out?"
        brandText="Confirm the email or phone number on your account and you can set a new password straight away."
      >
        <div className="authForm">
          <div className="dsFormMessage warning" role="alert">
            <ErrorOutlineIcon />
            <span>Your reset was interrupted. Ask for a new code to continue.</span>
          </div>
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => go(history, "/forgetPassword")}
          >
            Back to password reset
          </Button>
        </div>
      </AuthShell>
    );
  }

  const expired = remaining === 0;

  return (
    <AuthShell
      title="Enter your code"
      subtitle="We sent a five-digit code to confirm it is you."
      brandTitle="Locked out?"
      brandText="Confirm the email or phone number on your account and you can set a new password straight away."
    >
      <form className="authForm" onSubmit={onSubmit}>
        <div className="authRecap">
          <span className="authRecapValue">{identifier}</span>
          <button
            type="button"
            className="authLink"
            onClick={() => go(history, "/forgetPassword")}
          >
            Change
          </button>
        </div>

        {codeError && (
          <div className="dsFormMessage error" role="alert">
            <ErrorOutlineIcon />
            <span>{codeError}</span>
          </div>
        )}

        <CodeInput
          value={code}
          onChange={setCode}
          onComplete={(value) => {
            if (!expired) submit(value);
          }}
          error={Boolean(codeError)}
          disabled={checking || expired}
        />

        <div className="authCountdown">
          <span>
            {expired ? (
              <span className="authCountdownTime isExpired">Code expired</span>
            ) : (
              <React.Fragment>
                Expires in <span className="authCountdownTime">{formatSeconds(remaining)}</span>
              </React.Fragment>
            )}
          </span>
          <button
            type="button"
            className="authLink"
            onClick={onResend}
            disabled={resending || resendIn > 0}
          >
            {resending
              ? "Sending"
              : resendIn > 0
              ? `Resend in ${resendIn}s`
              : "Send a new code"}
          </button>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={checking}
          disabled={expired}
        >
          {checking ? "Checking" : "Continue"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default EnterForgetPasswordCode;
